import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Box, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Tooltip, Typography, Paper,
} from "@mui/material";
import {
  CheckCircleOutlineOutlined, DeleteOutline, EditOutlined, EditNoteOutlined,
  MenuBookOutlined, VisibilityOutlined,
} from "@mui/icons-material";
import apiService from "../../../service/Api";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
import StatsCard from "../../bookings/list-booking2/StatsCard";
import DeleteConfirmDialog from "../../bookings/list-booking2/DeleteConfirmDialog";
import type { AcademyOptions, PaginatedCourses } from "./types";
import {
  FilterSelect, ListPanel, PrimaryButton, SearchBox, StatusPill, tableHeadCellSx,
} from "./AcademyUi";

const countFor = (status?: string) => apiService
  .get<PaginatedCourses>("/api/lms/courses/", { status, page_size: 1 })
  .then((response) => response.data.count);

export default function AcademyCourseAdmin() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ACADEMY_MANAGE);
  const [data, setData] = useState<PaginatedCourses | null>(null);
  const [options, setOptions] = useState<AcademyOptions | null>(null);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0 });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    apiService.get<PaginatedCourses>("/api/lms/courses/", {
      search: search || undefined, category: category || undefined,
      status: status || undefined, page: page + 1, page_size: rowsPerPage,
    }).then((response) => setData(response.data))
      .catch(() => toast.error("Could not load courses."))
      .finally(() => setLoading(false));
  }, [search, category, status, page, rowsPerPage]);

  const loadStats = useCallback(() => {
    Promise.all([countFor(), countFor("published"), countFor("draft")])
      .then(([total, published, draft]) => setStats({ total, published, draft }))
      .catch(() => undefined);
  }, []);

  useEffect(() => { const timer = window.setTimeout(load, 300); return () => window.clearTimeout(timer); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { apiService.get<AcademyOptions>("/api/lms/courses/options/").then((r) => setOptions(r.data)); }, []);

  const filterChanged = <T,>(setter: (value: T) => void) => (value: T) => { setter(value); setPage(0); };

  const handleDelete = async () => {
    if (toDelete === null) return;
    try {
      await apiService.delete(`/api/lms/courses/${toDelete}/`);
      toast.success("Course deleted successfully");
      load(); loadStats();
    } catch {
      toast.error("Failed to delete course");
    } finally { setToDelete(null); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <StatsCard title="Total Courses" value={stats.total} icon={<MenuBookOutlined />} backgroundColor="#4285F4" />
          <StatsCard title="Published" value={stats.published} icon={<CheckCircleOutlineOutlined />} backgroundColor="#34A853" />
          <StatsCard title="Drafts" value={stats.draft} icon={<EditNoteOutlined />} backgroundColor="#F4A742" />
        </Box>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
        <ListPanel>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2, flexWrap: { xs: "wrap", md: "nowrap" } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: { xs: "wrap", md: "nowrap" }, minWidth: 0 }}>
              <SearchBox value={search} onChange={filterChanged(setSearch)} placeholder="Search courses..."
                minWidth={{ xs: "160px", sm: "220px" }} />
              <FilterSelect value={category} onChange={filterChanged(setCategory)} allLabel="All categories" width={145}
                options={(options?.categories ?? []).map((value) => ({ value, label: value }))} />
              <FilterSelect value={status} onChange={filterChanged(setStatus)} allLabel="All statuses" width={125}
                options={[{ value: "published", label: "Published" }, { value: "draft", label: "Draft" }]} />
            </Box>
            {canManage && (
              <PrimaryButton onClick={() => navigate("/academy/admin/courses/create")}>Add Course</PrimaryButton>
            )}
          </Box>

          {loading && !data ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress sx={{ color: "#4285F4" }} /></Box>
          ) : !data?.results.length ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>No courses found</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", overflowX: "auto", opacity: loading ? 0.6 : 1 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                    {["Course", "Category", "Content", "Target Groups", "Status", "Created", "Actions"].map((label) => (
                      <TableCell key={label} sx={tableHeadCellSx}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.results.map((course) => (
                    <TableRow key={course.id} hover sx={{ cursor: "pointer", "&:hover": { backgroundColor: "background.paper" } }}
                      onClick={() => navigate(`/academy/courses/${course.id}`)}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{course.title}</Typography>
                        {course.skills.length > 0 && (
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                            {course.skills.map((skill) => skill.name).join(", ")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell><Typography variant="body2" sx={{ color: "text.secondary" }}>{course.category || "—"}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{course.module_count} modules · {course.lesson_count} lessons</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {course.target_groups.length ? course.target_groups.map((group) => group.name).join(", ") : "All staff"}
                        </Typography>
                      </TableCell>
                      <TableCell><StatusPill status={course.status} /></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {new Date(course.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="View" arrow>
                            <IconButton size="small" sx={{ color: "#614438" }} onClick={() => navigate(`/academy/courses/${course.id}`)}>
                              <VisibilityOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" sx={{ color: "text.secondary" }} onClick={() => navigate(`/academy/admin/courses/${course.id}/edit`)}>
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton size="small" sx={{ color: "#F44336" }} onClick={() => setToDelete(course.id)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={data?.count ?? 0}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ borderTop: "1px solid #E0E0E0", "& .MuiTablePagination-toolbar": { justifyContent: "center" } }}
          />
        </ListPanel>
      </motion.div>

      <DeleteConfirmDialog
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this course? All of its modules, lessons and learner progress will be removed. This action cannot be undone."
      />
    </motion.div>
  );
}
